import { isEqual } from "lodash";
import { useEffect, useEffectEvent, useMemo, useRef, useState, useTransition } from "react";
import { Col, Form, Row } from "react-bootstrap";
import {
  IRequestStatus,
  IValidReturn,
  IValidationFunction,
  IValidationTarget,
  loadWrapper,
} from "../utility";
import { classMerge } from "../utility/classMerge";
import { AsyncButton } from "./AsyncButton";
import "./ControlTypeAhead.css";
import { DropdownItem, DropdownPortal } from "./DropdownPortal";

/**
 * Interface for typeahead list items.
 */
export interface TypeAheadItem {
  value: string;
}

/**
 * Props for the ControlTypeAhead component.
 */
export interface ControlTypeAheadProps {
  id: string;
  availableList: TypeAheadItem[];
  backgroundColor?: string;
  buttonLabel?: string;
  disabled?: boolean;
  label: string;
  labelWidth?: number;
  maxWidthCols?: number;
  onButtonClick?: () => void;
  pauseCoverText?: string;
  placeHolder?: string;
  requestStatus?: IRequestStatus<unknown> | IRequestStatus<unknown>[];
  setValue: (ret: string) => void;
  spellCheck?: boolean;
  validCondition?: IValidationFunction;
  validationReturn?: (ret: boolean) => void;
  value: string;
}

/**
 * A typeahead input component that provides autocomplete functionality with a filterable
 * dropdown of available options.
 * Features include keyboard navigation, optional action button, and responsive layout with Bootstrap grid system.
 *
 * @param props - Component props.
 * @param props.id - Unique identifier for the typeahead control
 * @param props.availableList - Array of available options for typeahead suggestions
 * @param props.backgroundColor - Background color for the control (defaults to "var(--bs-body-bg)")
 * @param props.buttonLabel - Label text for the optional action button (defaults to "OK")
 * @param props.disabled - Whether the control is disabled
 * @param props.label - Label text for the input field
 * @param props.labelWidth - Width of the label column in Bootstrap grid units (defaults to 2)
 * @param props.maxWidthCols - Maximum width in Bootstrap grid columns (defaults to 12)
 * @param props.onButtonClick - Callback function for button click action
 * @param props.pauseCoverText - Text to display when control is paused/loading
 * @param props.placeHolder - Placeholder text for the input
 * @param props.requestStatus - Request status for async operations
 * @param props.setValue - Callback function to update the value
 * @param props.spellCheck - Whether spell checking is enabled (defaults to true)
 * @param props.validCondition - Optional validation function for custom valid or warning states
 * @param props.validationReturn - Optional callback that receives the latest validation result
 * @param props.value - Current value of the input
 */
export const ControlTypeAhead = ({
  id,
  availableList,
  backgroundColor = "var(--bs-body-bg)",
  buttonLabel = "OK",
  disabled,
  label,
  labelWidth = 2,
  maxWidthCols = 12,
  onButtonClick,
  pauseCoverText,
  placeHolder,
  requestStatus,
  setValue,
  spellCheck = true,
  validCondition,
  validationReturn,
  value,
}: ControlTypeAheadProps): React.ReactElement => {
  // Current value in the control
  const [currentValue, setCurrentValue] = useState<string>(value);
  const [_isPending, startTransition] = useTransition();
  useEffect(() => startTransition(() => setCurrentValue(value)), [value]);
  const [validationStatus, setValidationStatus] = useState<IValidReturn>({
    success: true,
    data: {
      valid: true,
      validationText: "",
    },
  });
  const [validationRequest, setValidationRequest] = useState<IRequestStatus<IValidationTarget>>({
    requesting: false,
    error: false,
  });

  // Input ref for dropdown positioning
  const inputRef = useRef<HTMLInputElement>(null);

  // Currently available values
  const currentList = availableList.filter((i) =>
    i.value.toLowerCase().includes(currentValue.toLowerCase()),
  );
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  // Show dropdown state
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const validationTargetOptions = useMemo(
    () => ({
      existingValues: availableList.map((item) => item.value),
    }),
    [availableList],
  );

  /**
   * Checks validation for the current value.
   *
   * @param newValue - The validation target containing the value to check.
   */
  const checkValidation = useEffectEvent(async (newValue: IValidationTarget) => {
    if (!validCondition) {
      setValidationStatus({
        success: true,
        data: {
          valid:
            newValue.value !== "" &&
            validationTargetOptions.existingValues.includes(newValue.value),
          validationText: "",
        },
      });
      setValidationRequest({ requesting: false, error: false, requestedId: newValue });
      validationReturn?.(
        newValue.value !== "" && validationTargetOptions.existingValues.includes(newValue.value),
      );
    } else {
      const response = await loadWrapper(
        validCondition,
        newValue,
        { ...validationRequest, error: false, errorText: undefined },
        setValidationRequest,
        "",
      );
      /* istanbul ignore else */
      if (response) {
        setValidationStatus(response);
        validationReturn?.(response.success === false ? false : response.data.valid);
      }
    }
  });

  useEffect(() => {
    if (
      !validationRequest.requesting &&
      (currentValue !== validationRequest.requestedId?.value ||
        !isEqual(validationTargetOptions, validationRequest.requestedId?.options))
    ) {
      startTransition(() => {
        checkValidation({ value: currentValue, options: validationTargetOptions });
      });
    }
  }, [currentValue, validationRequest, validationTargetOptions]);

  // Convert available items to dropdown items
  const dropdownItems: DropdownItem[] = currentList.map((item, index) => ({
    id: `${id}-item-${index}`,
    text: item.value,
    data: item,
  }));

  /**
   * Handle return
   *
   * @param e - FocusEvent<HTMLInputElement>
   */
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const n = e.currentTarget.value.replace(/\s+/g, " ").trim();
    setCurrentValue(n);

    // Set value if valid or if no validation function provided
    const isValidValue = validCondition
      ? true
      : n !== "" && validationTargetOptions.existingValues.includes(n);
    if (n !== value && isValidValue) {
      setValue(n);
    }
    setShowDropdown(false);
  };

  // Handle dropdown item selection
  /**
   * Handles selection of an item from the dropdown.
   *
   * @param item - The selected dropdown item
   */
  const handleDropdownSelect = (item: DropdownItem) => {
    const selectedValue = item.text;
    setCurrentValue(selectedValue);
    /* istanbul ignore else */
    if (value !== selectedValue) setValue?.(selectedValue);
    setShowDropdown(false);
    setSelectedIndex(-1);
  };

  /**
   * Handle typing
   *
   * @param e - React.KeyboardEvent<HTMLInputElement>
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && showDropdown && dropdownItems.length > 0) {
      e.preventDefault();

      /* istanbul ignore else */
      if (selectedIndex < dropdownItems.length && selectedIndex >= 0) {
        handleDropdownSelect(dropdownItems[selectedIndex]);
      }
    } else if (e.key === "Enter") {
      inputRef.current?.blur();
    } else if (e.key === "Escape" && showDropdown && dropdownItems.length > 0) {
      setShowDropdown(false);
      setSelectedIndex(-1);
    } else if (e.key === "Escape") {
      setCurrentValue(value);
      setTimeout(() => {
        inputRef.current?.blur();
      }, 1);
    }
  };

  /**
   * Handle input changes.
   *
   * @param e - React.ChangeEvent<HTMLInputElement>
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentValue(e.currentTarget.value);
    setShowDropdown(e.currentTarget.value.trim().length > 0);
    setSelectedIndex(-1);
  };

  /**
   * Handle input focus
   */
  const handleFocus = () => {
    setShowDropdown(
      currentValue.trim().length > 0 &&
        (!validationStatus.success || !validationStatus.data.valid) &&
        currentList.length > 0,
    );
  };

  return (
    <Form.Group
      controlId={`${id}`}
      as={Row}
      className="my-1"
    >
      {labelWidth > 0 && (
        <Form.Label
          column
          md={labelWidth}
        >
          {label}
        </Form.Label>
      )}
      <Col md={maxWidthCols - labelWidth - (onButtonClick ? 2 : 0)}>
        <>
          <Form.Control
            ref={inputRef}
            size="sm"
            value={currentValue}
            disabled={disabled || !setValue}
            onChange={handleChange}
            placeholder={placeHolder}
            className={classMerge([
              validationStatus.success && validationStatus.data.warning
                ? "is-warning"
                : currentValue !== "" && validationStatus.success && validationStatus.data.valid
                  ? "is-valid"
                  : currentValue !== "" &&
                      (validationStatus.success === false ||
                        (validationStatus.success && !validationStatus.data.valid))
                    ? "is-invalid"
                    : "",
            ])}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            spellCheck={spellCheck}
          />
          {validationStatus.success &&
            validationStatus.data.validationText &&
            (validationStatus.data.warning || !validationStatus.data.valid) && (
              <Form.Text
                className={
                  validationStatus.data.warning
                    ? "warning-feedback d-block"
                    : "invalid-feedback d-block"
                }
              >
                {validationStatus.data.validationText}
              </Form.Text>
            )}
          <DropdownPortal
            isOpen={showDropdown && dropdownItems.length > 0 && !disabled}
            items={dropdownItems}
            onSelect={handleDropdownSelect}
            onClose={() => {
              setShowDropdown(false);
              setSelectedIndex(-1);
            }}
            anchorRef={inputRef}
            backgroundColor={backgroundColor}
            selectedIndex={selectedIndex}
            onSelectedIndexChange={setSelectedIndex}
          />
        </>
      </Col>
      {onButtonClick && (
        <Col md={2}>
          <AsyncButton
            id={`${id}-button`}
            disabled={!(validationStatus.success && validationStatus.data.valid)}
            onClick={onButtonClick}
            size="sm"
            requestStatus={requestStatus}
            pauseCoverText={pauseCoverText}
          >
            {buttonLabel}
          </AsyncButton>
        </Col>
      )}
    </Form.Group>
  );
};

ControlTypeAhead.displayName = "ControlTypeAhead";
